// One definition of "attendance rate", because there were three.
//
// The HR analytics screen computed this metric inline in three places — the
// headline tile, the 8-week series and the heatmap — and they disagreed:
//
//   • the tile divided by present+wfh+absent (holidays and leave excluded);
//   • the series divided by "not a holiday", so a week containing approved
//     leave was charged as absence;
//   • all three divided hours by the ORG standard day, so anyone on a
//     non-default contract was scored against someone else's day — a
//     6h-contracted employee who worked a full 6h showed 75%.
//
// Two rules, stated once:
//   1. A day counts FRACTIONALLY, against that person's contracted day.
//   2. Approved leave is not absence. It leaves the denominator entirely,
//      exactly like a holiday — it must not sit in the denominator scoring 0.

export type AttendanceRow = {
  profile_id: string;
  status: string;
  hours: number | null;
};

/** Hours to treat as a full day when a person has no contracted day set. */
const FALLBACK_DAY = 8;

/**
 * How much of a working day this row represents, 0..1.
 * A 3h day against a 6h contract is 0.5, not 1 — and never more than 1, so
 * overtime cannot push an average above 100%.
 */
export const presentFraction = (row: AttendanceRow, dayHours: number): number =>
  row.status === 'present' || row.status === 'wfh'
    ? Math.min(1, (row.hours || 0) / (dayHours > 0 ? dayHours : FALLBACK_DAY))
    : 0;

/**
 * Was this day one the person was expected to work?
 * Holidays and approved leave are not — counting either as a missed day is
 * what made the rate look broken.
 */
export const isScheduled = (row: AttendanceRow): boolean =>
  row.status !== 'holiday' && row.status !== 'leave';

/**
 * Attendance rate as a whole percentage (0-100), or null when nobody was
 * scheduled.
 *
 * Not-measured is not zero (UI-CONVENTIONS §7). The 8-week trend plotted a week
 * with no attendance rows as 0%, so six weeks before the data began drew as a
 * six-week collapse in attendance (HRA-14). A series or a tile takes this one;
 * `attendanceRate` below is for the callers that have already established there
 * is something to score.
 *
 * `dayFor` resolves a profile's contracted day; pass the org standard as the
 * fallback for anyone without one.
 */
export const attendanceRateOrNull = (
  rows: readonly AttendanceRow[],
  dayFor: (profileId: string) => number,
): number | null => {
  const scheduled = rows.filter(isScheduled).length;
  if (!scheduled) return null;
  const present = rows.reduce((sum, r) => sum + presentFraction(r, dayFor(r.profile_id)), 0);
  return Math.round((present / scheduled) * 100);
};

/** The same rate, with 0 rather than NaN when nobody was scheduled. */
export const attendanceRate = (
  rows: readonly AttendanceRow[],
  dayFor: (profileId: string) => number,
): number => attendanceRateOrNull(rows, dayFor) ?? 0;

/**
 * A rate's change against the window before it, in percentage points, for a
 * KpiTile's `delta` ("+5 pts vs previous 28 days"). Both figures come from
 * attendanceRateOrNull over the same rows, so the change can't disagree with
 * the figure it sits under. No figure now, no change. No figure before is a
 * null `delta`, for the caller's stub, not "0 pts": nothing to compare is not
 * "no change" (UI-CONVENTIONS §7). Zero is untoned and points nowhere.
 *
 * Signed, + or U+2212 as fmtDelta signs a correction: the tile's triangle and
 * colour are aria-hidden, so the sign is the direction a screen reader hears.
 * Unsigned, a rise and a fall were both "5 pts" read aloud.
 */
export const rateChange = (
  now: number | null,
  before: number | null,
  against: string,
): { delta: string | null; trend: 'up' | 'down' | 'neutral' } | null => {
  if (now == null) return null;
  if (before == null) return { delta: null, trend: 'neutral' };
  const d = now - before;
  if (d === 0) return { delta: `no change vs ${against}`, trend: 'neutral' };
  const pts = Math.abs(d);
  return {
    delta: `${d > 0 ? '+' : '−'}${pts} pt${pts === 1 ? '' : 's'} vs ${against}`,
    trend: d > 0 ? 'up' : 'down',
  };
};
